import { useContext, useId } from 'react';
import {
  ComponentWithAs,
  IconProps,
  MenuButton,
  forwardRef,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { Box, Flex } from '@allergan-data-labs/alle-elements-layout';
import { ChevronDownIcon } from '@allergan-data-labs/alle-elements-icons/20/chevronDownIcon';
import { ChevronUpIcon } from '@allergan-data-labs/alle-elements-icons/20/chevronUpIcon';
import { OverflowIcon as OverflowIcon16 } from '@allergan-data-labs/alle-elements-icons/16/overflowIcon';
import { OverflowIcon as OverflowIcon20 } from '@allergan-data-labs/alle-elements-icons/20/overflowIcon';
import { OverflowIcon as OverflowIcon24 } from '@allergan-data-labs/alle-elements-icons/24/overflowIcon';

/**
 * ESLint is reporting a circular dependency error:
 *  - `Circular dependency between "button" and "action-menu" detected: button -> action-menu -> button`
 * We have disabled @nx/enforce-module-boundaries for this import because the `ActionMenu` component is not part of the `Button` package.
 * While it is used in the stories and spec files, it is not included in the package distribution.
 */
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ActionListItem,
  ActionMenu,
  ActionMenuContent,
  ActionMenuContext,
  type ActionListItemProps,
  type ActionMenuProps,
} from '@allergan-data-labs/alle-elements-action-menu';

import {
  getButtonContentStyles,
  getLabelButtonActiveStyles,
  getOverflowButtonActiveStyles,
} from './button-select.theme';
import { Button, ButtonSizes, type ButtonProps } from './button';
import { IconButton } from './icon-button';

export type ButtonSelectVariants = 'default' | 'overflow';

export interface ButtonSelectProps
  extends Omit<
    ActionMenuProps,
    'triggerElement' | 'triggerText' | 'triggerElementProps' | 'isSubmenu'
  > {
  /**
   * The color mode that `ButtonSelect` will display.
   * @default light
   */
  colorMode?: ColorMode;

  /**
   * The label for the `ButtonSelect`. If variant is `default`, this will be the text displayed on the trigger button.
   * otherwise, it will be used as the aria-label for the trigger button.
   */
  label?: string;

  /**
   * Props to pass to the trigger button. This is a pass-through prop to the `Button` component.
   * See [Button](/?path=/docs/components-buttons-button--docs)
   */
  buttonProps?: Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'colorMode'>;

  /**
   * The variant of the `ButtonSelect`. This determines the appearance of the trigger button.
   * @default default
   */
  variant?: ButtonSelectVariants;

  /**
   * The contents of the `ButtonSelect`.
   */
  children:
    | React.ReactElement<typeof ButtonSelectItem>
    | React.ReactElement<typeof ButtonSelectItem>[];
}

export type ButtonSelectItemProps = ActionListItemProps;

const LabelButton = forwardRef<Omit<ButtonSelectProps, 'children'>, 'button'>(
  ({ label, buttonProps, colorMode }, ref) => {
    const { isOpen } = useContext(ActionMenuContext);
    const id = useId();
    const mapGap: Record<ButtonSizes, string> = {
      lg: '16',
      md: '8',
      sm: '8',
      xs: '6',
    };
    return (
      <MenuButton
        aria-labelledby={`menu-button-label-${id}`}
        ref={ref}
        as={Button}
        colorMode={colorMode}
        sx={getLabelButtonActiveStyles(
          buttonProps?.variant || 'solid',
          buttonProps?.colorScheme || 'action',
          colorMode || 'light'
        )}
        {...buttonProps}
      >
        <Flex sx={getButtonContentStyles(mapGap[buttonProps?.size || 'md'])}>
          {label && <span id={`menu-button-label-${id}`}>{label}</span>}
          {isOpen ? (
            <ChevronUpIcon aria-label={`${label} opened`} />
          ) : (
            <ChevronDownIcon aria-label={`${label} closed`} />
          )}
        </Flex>
      </MenuButton>
    );
  }
);

const getOverflowIcon = (buttonSize: ButtonSizes = 'md') => {
  const OverflowIconComponent = (
    {
      lg: OverflowIcon24,
      md: OverflowIcon24,
      sm: OverflowIcon20,
      xs: OverflowIcon16,
    } satisfies Record<ButtonSizes, (props: IconProps) => JSX.Element>
  )[buttonSize];
  return OverflowIconComponent;
};

const OverflowButton = forwardRef<
  Omit<ButtonSelectProps, 'children'>,
  'button'
>(({ label, buttonProps, colorMode }, ref) => {
  const id = useId();
  const OverflowIcon = getOverflowIcon(buttonProps?.size);

  return (
    <MenuButton
      aria-label={label || id}
      ref={ref}
      as={IconButton}
      icon={
        <OverflowIcon
          aria-label={`overflow icon${buttonProps?.size} ${label || id}`}
        />
      }
      colorMode={colorMode}
      sx={getOverflowButtonActiveStyles(
        buttonProps?.variant || 'solid',
        buttonProps?.colorScheme || 'action',
        colorMode || 'light'
      )}
      {...buttonProps}
    />
  );
});

export const ButtonSelectItem = forwardRef<ButtonSelectItemProps, 'div'>(
  (props: React.PropsWithChildren<ButtonSelectItemProps>, ref) => {
    return <ActionListItem {...props} ref={ref} />;
  }
);

/**
 * `ButtonSelect` is a dropdown menu button that allows users to select from a list of options.
 * It leverages the [ActionMenu](?path=/docs/components-action-menu-action-menu--docs) component to provide context state and focus management
 * and [Button](?path=/docs/components-buttons-button--docs) component to display the `ActionMenu` items.
 * This component uses the `ButtonSelectItem` which is a wrapper around the [ActionListItem](?path=/docs/action-list-item--docs)
 * component to provide a consistent API for the `ButtonSelect`.
 */
export const ButtonSelect = forwardRef<ButtonSelectProps, 'div'>(
  (
    {
      colorMode: propColorMode = 'light',
      label = 'Select',
      variant = 'default',
      buttonProps,
      children,
      ...rest
    }: ButtonSelectProps,
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    const TriggerButton = (
      {
        default: LabelButton,
        overflow: OverflowButton,
      } satisfies Record<
        ButtonSelectVariants,
        ComponentWithAs<'button', ButtonSelectProps>
      >
    )[variant];

    return (
      <Box ref={ref}>
        <ActionMenu colorMode={colorMode} {...rest}>
          <TriggerButton
            label={label}
            colorMode={colorMode}
            buttonProps={buttonProps}
          />
          <ActionMenuContent>{children}</ActionMenuContent>
        </ActionMenu>
      </Box>
    );
  }
);
